import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronRight, Sparkles } from "lucide-react";
import { scrollToId } from "../lib/lenis";
import { ease } from "../motion";
import { IMAGES } from "../data";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay, ease }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const frameRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 130, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 130, damping: 16 });
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [50, -70]);

  const onMove = (event) => {
    const rect = frameRef.current.getBoundingClientRect();
    mx.set((event.clientX - rect.left) / rect.width - 0.5);
    my.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section className="hero" data-testid="hero-section">
      <div className="hero-copy">
        <motion.p className="eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.05, ease }}>
          <span className="eyebrow-dot" /> Vanguard · Performance Partners
        </motion.p>
        <h1 className="hero-title" data-testid="hero-title">
          <MaskLine delay={0.15}>Marketing que</MaskLine>
          <MaskLine delay={0.3}><em>move ponteiros,</em></MaskLine>
          <MaskLine delay={0.45}>não vaidade.</MaskLine>
        </h1>
        <motion.p className="hero-lead" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6, ease }}>
          Estratégia, criatividade e dados para marcas que não querem apenas aparecer — querem crescer com previsibilidade.
        </motion.p>
        <motion.div className="hero-actions" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.72, ease }}>
          <button className="btn-gold" onClick={() => scrollToId("contact")} data-testid="hero-book-button">Quero crescer <ArrowUpRight size={17} /></button>
          <button className="btn-ghost" onClick={() => scrollToId("services")} data-testid="hero-services-button">Explorar soluções <ChevronRight size={16} /></button>
        </motion.div>
        <motion.div className="hero-proof" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.9 }}>
          <div className="avatar-stack"><span>MC</span><span>RA</span><span>JS</span></div>
          <p><strong>+120 marcas</strong> já aceleraram com a Vanguard</p>
        </motion.div>
      </div>

      <motion.div className="hero-visual" initial={{ opacity: 0, scale: 0.96, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.35, ease }}>
        <motion.div ref={frameRef} className="hero-frame" style={{ rotateX, rotateY, y: parallaxY, transformPerspective: 1000 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} data-testid="hero-visual-frame">
          <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />
          <img src={IMAGES.hero} alt="Equipe de marketing em reunião estratégica" />
          <div className="image-shade" />
          <div className="hero-stat" data-testid="hero-stat">
            <span className="stat-label">Crescimento médio</span>
            <strong>+184%</strong>
            <span className="stat-sub">em receita / ano</span>
          </div>
        </motion.div>
        <div className="visual-caption"><Sparkles size={14} /><span>Ideias que viram resultado</span><span className="caption-number">/ 01</span></div>
      </motion.div>
    </section>
  );
};
